import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthApi } from '../services/api';
import { 
  PuzzlePieceIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  XCircleIcon,
  PlusIcon,
  CogIcon,
  LinkIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  ArrowPathIcon,
  ChartBarIcon,
  UserIcon,
  CloudIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface IntegrationHealth {
  integration: string;
  healthy: boolean;
  message?: string;
  error?: string;
  server_url?: string;
  user?: string;
  rate_limit?: {
    remaining: number;
    limit: number;
    reset: string;
  };
  server_info?: {
    version: string;
    build_number: string;
    server_title: string;
  };
  space?: string;
  space_key?: string;
  timestamp?: string;
}

interface Integration {
  id: string;
  name: string;
  type: 'jira' | 'github' | 'confluence' | 'slack' | 'figma' | 'custom';
  description: string;
  status: 'connected' | 'disconnected' | 'error' | 'checking';
  logo: string;
  features: string[];
  health?: IntegrationHealth;
  lastChecked?: string;
  responseTime?: number;
  isConfigured: boolean;
  configuredFrom: 'environment' | 'database' | 'none';
}

const Integrations: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const queryClient = useQueryClient();

  // Check which integrations are configured from environment
  const configuredIntegrations = [
    {
      id: 'jira',
      name: 'Jira',
      type: 'jira' as const,
      description: 'Connect to Jira to manage issues, create tickets, and track project progress',
      logo: '🎯',
      features: ['Create Issues', 'Update Status', 'Search Issues', 'Project Management', 'Task Comments'],
      isConfigured: true,
      configuredFrom: 'environment' as const
    },
    {
      id: 'confluence',
      name: 'Confluence',
      type: 'confluence' as const,
      description: 'Create and manage documentation, requirements, and technical designs',
      logo: '📄',
      features: ['Create Pages', 'Update Content', 'Search Documentation', 'Link to Jira', 'Collaboration'],
      isConfigured: true,
      configuredFrom: 'environment' as const
    },
    {
      id: 'github',
      name: 'GitHub',
      type: 'github' as const,
      description: 'Manage repositories, create pull requests, and handle code collaboration',
      logo: '🔗',
      features: ['Repository Access', 'Pull Requests', 'Issue Management', 'Branch Operations', 'Code Analysis'],
      isConfigured: true,
      configuredFrom: 'environment' as const
    },
    {
      id: 'figma',
      name: 'Figma',
      type: 'figma' as const,
      description: 'Design collaboration and asset management platform',
      logo: '🎨',
      features: ['Design Access', 'Component Library', 'Collaboration', 'Asset Export'],
      isConfigured: true,
      configuredFrom: 'environment' as const
    },
    {
      id: 'slack',
      name: 'Slack',
      type: 'slack' as const,
      description: 'Team communication and notification management',
      logo: '💬',
      features: ['Send Messages', 'Channel Management', 'Notifications', 'File Sharing'],
      isConfigured: false,
      configuredFrom: 'none' as const
    }
  ];

  // Fetch integration health status
  const { data: healthData, refetch: refetchHealth, isLoading: healthLoading } = useQuery({
    queryKey: ['integrationHealth'],
    queryFn: healthApi.getAllHealth,
    refetchInterval: autoRefresh ? 30000 : false, // Refresh every 30 seconds if auto-refresh is enabled
  });

  // Combine configuration with health data
  const integrations: Integration[] = configuredIntegrations.map(config => {
    const health = healthData?.find(h => h.integration === config.type);
    const status = health ? (health.healthy ? 'connected' : 'error') : 'disconnected';
    
    return {
      ...config,
      status: healthLoading ? 'checking' : status,
      health,
      lastChecked: health?.timestamp || new Date().toISOString(),
      responseTime: Math.floor(Math.random() * 300) + 50, // Mock response time
    };
  });

  // Manual health check mutation
  const healthCheckMutation = useMutation({
    mutationFn: healthApi.getSpecificHealth,
    onSuccess: () => {
      toast.success('Health check completed');
      refetchHealth();
    },
    onError: (error) => {
      toast.error(`Health check failed: ${error.message}`);
    },
  });

  const categories = [
    { id: 'all', name: 'All Integrations', count: integrations.length },
    { id: 'connected', name: 'Connected', count: integrations.filter(i => i.status === 'connected').length },
    { id: 'development', name: 'Development', count: integrations.filter(i => ['jira', 'github', 'confluence'].includes(i.type)).length },
    { id: 'communication', name: 'Communication', count: integrations.filter(i => ['slack'].includes(i.type)).length },
    { id: 'design', name: 'Design', count: integrations.filter(i => ['figma'].includes(i.type)).length },
  ];

  const filteredIntegrations = integrations.filter(integration => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'connected') return integration.status === 'connected';
    if (selectedCategory === 'development') return ['jira', 'github', 'confluence'].includes(integration.type);
    if (selectedCategory === 'communication') return ['slack'].includes(integration.type);
    if (selectedCategory === 'design') return ['figma'].includes(integration.type);
    return true;
  });

  const getStatusIcon = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'checking':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <ExclamationCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
      case 'error':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'checking':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              🔌 Integrations
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage your third-party connections and monitor their health status
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                autoRefresh 
                  ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
              }`}
            >
              <ArrowPathIcon className={`h-4 w-4 inline mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
              Auto Refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={() => refetchHealth()}
              disabled={healthLoading}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 inline mr-2 ${healthLoading ? 'animate-spin' : ''}`} />
              Refresh All
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Connected</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {integrations.filter(i => i.status === 'connected').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Math.round(integrations.reduce((acc, i) => acc + (i.responseTime || 0), 0) / integrations.length)}ms
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <BoltIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Configured</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {integrations.filter(i => i.isConfigured).length}/{integrations.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
              <CloudIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Check</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatTimestamp(healthData?.[0]?.timestamp)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              selectedCategory === category.id
                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700'
            }`}
          >
            {category.name} ({category.count})
          </button>
        ))}
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => (
          <div
            key={integration.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{integration.logo}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {integration.configuredFrom === 'environment' ? '🔧 Environment' : '⚙️ Manual'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(integration.status)}
                  <button
                    onClick={() => healthCheckMutation.mutate(integration.type)}
                    disabled={healthCheckMutation.isPending}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Check health"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${healthCheckMutation.isPending ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Status Badge */}
              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border mb-4 ${getStatusColor(integration.status)}`}>
                {integration.status === 'checking' ? 'Checking...' : integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
              </div>

              {/* Health Info */}
              {integration.health && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  {integration.health.healthy ? (
                    <div className="space-y-1">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        ✅ {integration.health.message}
                      </p>
                      {integration.health.user && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <UserIcon className="h-3 w-3 inline mr-1" />
                          User: {integration.health.user}
                        </p>
                      )}
                      {integration.health.space && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <CloudIcon className="h-3 w-3 inline mr-1" />
                          Space: {integration.health.space} ({integration.health.space_key})
                        </p>
                      )}
                      {integration.health.rate_limit && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <ChartBarIcon className="h-3 w-3 inline mr-1" />
                          Rate limit: {integration.health.rate_limit.remaining}/{integration.health.rate_limit.limit}
                        </p>
                      )}
                      {integration.health.server_info && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <BoltIcon className="h-3 w-3 inline mr-1" />
                          Version: {integration.health.server_info.version}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-700 dark:text-red-400">
                      ❌ {integration.health.error || 'Connection failed'}
                    </p>
                  )}
                </div>
              )}

              {/* Description */}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {integration.description}
              </p>

              {/* Features */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Features:</p>
                <div className="flex flex-wrap gap-1">
                  {integration.features.slice(0, 3).map((feature, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                    >
                      {feature}
                    </span>
                  ))}
                  {integration.features.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                      +{integration.features.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <ClockIcon className="h-3 w-3" />
                  <span>Last checked: {formatTimestamp(integration.lastChecked)}</span>
                </div>
                {integration.responseTime && (
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${
                      integration.responseTime < 200 ? 'bg-green-400' : 
                      integration.responseTime < 500 ? 'bg-yellow-400' : 'bg-red-400'
                    }`} />
                    <span>{integration.responseTime}ms</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <div className="text-center py-12">
          <PuzzlePieceIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No integrations found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your filters or add a new integration.
          </p>
        </div>
      )}
    </div>
  );
};

export default Integrations; 